/**
 * Single-panel Sidebar with module dropdown picker at top.
 * Replaces the two-tier layout (module rail + secondary nav) with:
 * - Module dropdown trigger at top (icon + name + chevron → opens picker)
 * - Per-module navigation items (including Google-conditional items)
 * Collapses to icon-only (56px) on unhover, expands (224px) on hover.
 */

import { useState, useEffect, useRef, useMemo, Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bot,
  ExternalLink,
  Globe,
  ChevronDown,
  ChevronUp,
  Folder,
  FolderOpen,
  ClipboardList,
  LayoutDashboard,
  Target,
  Timer,
  Settings,
  ShieldCheck,
  ArrowLeft,
  ArrowRight,
  Rocket,
  Grip,
  MoreHorizontal,
  Check,
  Pencil,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '../ui/sheet';
import { useModule } from '../../shared/contexts/ModuleContext';
import {
  ModuleConfig,
  getAccessibleTenantSettingsPath,
  getModuleById,
} from '../../shared/config/modules.config';
import { NavItem } from '../../shared/config/modules.config';
import { useAgents, useAvailableAvatars } from '../../modules/ai_agents/hooks/useAgents';
import { ApprovalsNavItem } from '../../modules/ai_agents/components/ApprovalsNavItem';
import { useWorkspaces, useDeleteWorkspace } from '../../modules/tasks/hooks/useWorkspaces';
import { useBoards, useSharedBoards } from '../../modules/tasks/hooks/useBoards';
import { BoardDeleteDialog } from '../../modules/tasks/components/BoardDeleteDialog';
import { useWorkManagementDashboard } from '../../modules/tasks/hooks/useDashboard';
import { ShareMenuItem } from '../../shared/components/permissions';
import { ConfirmDialog, BidiText, AppNavLink } from '../../shared/components/common';
import { autoDirection, documentDirection } from '../../shared/utils/bidi';
import { toast } from 'sonner';
import { useSalesSetupStatus } from '../../modules/pipeline/hooks/useDashboard';
import { useAuth } from '../../shared/auth';
import { useModuleAdminCheck } from '../../shared/hooks/useIsModuleAdmin';
import { useNavItemAccess } from '../../shared/hooks/useNavItemAccess';
import { useGuardedNavigate } from '../../shared/hooks/useGuardedNavigate';
import { isListedNavItem, isNavItemVisible } from '../../shared/utils/navItemAccess';
import { resolveModuleNameKey } from '../../shared/hooks/useModuleName';
import { ModuleUserSettingsDialog } from './ModuleUserSettingsDialog';
import { ModulePickerContent, DynamicPickerItem } from './ModulePickerContent';
import { ModuleSwitcher } from './ModuleSwitcher';
import { cn } from '../../lib/utils';
import { NAV_ITEM_SELECTED, NAV_ITEM_SELECTED_BG, NAV_SECTION_LABEL } from '../../shared/styles/sidebar';
import {
  SIDEBAR_WIDTH,
} from '../../shared/styles/layout.constants';
import { useModuleUsage } from '../../shared/hooks/useModuleUsage';
import { getSecondaryPanelBg, MODULE_ACCENT_COLOR } from '../../shared/utils/color';
import { isTauriDesktop } from '../../shared/utils/platform';
import { useThemeMode } from '../../shared/contexts/ThemeContext';
import appConfig from '../../shared-config/app.config.json';
import { getWorkspaceOrigin } from '../../shared/utils/workspaceOrigin';

// Re-export for backwards compatibility
const DRAWER_WIDTH = SIDEBAR_WIDTH;
const ONBOARDING_DISMISSED_KEY = 'tasks-sidebar-onboarding-dismissed';



interface SidebarProps {
  open: boolean;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
  anchor?: 'left' | 'right';
  /** When true, sidebar shows icon-only and expands on hover */
  collapsed?: boolean;
}

export function Sidebar({ open, onClose, variant = 'permanent', anchor = 'left', collapsed: collapsedProp = false }: SidebarProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  // Every sidebar ENTRY is now an `<AppNavLink>` (a real `<a href>`, so the browser
  // can open it in a new tab), which guards itself and is additionally visible to the
  // anchor-click interceptor in useUnsavedChangesWarning. This navigator is what is
  // left: the module picker's page/home selections, and the redirect after deleting
  // the workspace or board you were looking at. Guarding the component's ONE navigate
  // still covers whichever call site a later edit adds.
  const navigate = useGuardedNavigate();
  const { currentModule, switchModule, pickerModules, disabledPickerModules, pinnedModules, isCurrentModule, isModuleEnabledForUser } = useModule();
  const { recordModuleVisit, isFavorite, toggleFavorite, sortModules, refresh } = useModuleUsage();
  const { user } = useAuth();
  // Agent names and titles are tenant-authored, so their language is unknown —
  // read the document direction once here and resolve each per-agent.
  const direction = documentDirection();
  // Detect if running in desktop app
  const isDesktopApp = isTauriDesktop();

  // Module dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [moduleSearchQuery, setModuleSearchQuery] = useState('');

  // Everything the per-nav-item gates are evaluated against (roles, connected
  // Google/Microsoft accounts, Forwarder TMS persona). Shared with the module
  // picker and global search so all three agree on what this user may see.
  const navItemAccess = useNavItemAccess();
  // Tenant persona (Forwarder TMS) also drives persona-aware module naming.
  const sellsFreightServices = navItemAccess.sellsFreightServices;
  // Persona-aware module display name (e.g. Shipments → "Freight Operations").
  const getModuleName = (m: { id: string; nameKey: string }) =>
    t(resolveModuleNameKey(m, sellsFreightServices));

  // Collapsed state: icon-only sidebar that expands on hover
  const [hovered, setHovered] = useState(false);
  const isCollapsed = collapsedProp && !hovered;

  // Module-specific checks
  const isAIAgentsModule = currentModule.id === 'ai';
  const isWorkModule = currentModule.id === 'tasks';
  const isRecruitingModule = currentModule.id === 'recruiting';
  const isSalesModule = currentModule.id === 'pipeline';
  const hasUserSettings = currentModule.settings?.hasUserSettings === true;
  const userSettingsPath = currentModule.settings?.userSettingsPath;
  // A userSettingsPath may carry a query string (accounting's notification block
  // lives at /accounting/setup?section=notifications). Compare on the pathname
  // alone, or the gear never shows as active on those modules.
  const userSettingsPathname = userSettingsPath?.split('?')[0];
  const hasTenantSettings = currentModule.settings?.hasTenantSettings === true;
  // Mirrors require_module_admin() — including the T11 module_access_levels arm
  // that a hand-rolled `module_admin_roles` test silently drops. A tenant admin
  // is already one of its three arms, so `is_admin` is not re-tested here. The config→
  // permission module mapping comes from `ModuleConfig.permissionModules` (the
  // config JSON's own key by default), not a local table that could drift; the
  // check resolves those spellings onto both grant lists.
  //
  // The shield's destination is resolved together with its visibility, because a
  // module administered in sub-scopes has one settings area per scope and
  // `tenantSettingsPath` names only the first: master_data's catalog. Showing the
  // shield module-wide and always linking there would send a contacts admin to a
  // page they get bounced off.
  const moduleAdminCheck = useModuleAdminCheck();
  const tenantSettingsPath = hasTenantSettings
    ? getAccessibleTenantSettingsPath(currentModule.id, moduleAdminCheck)
    : null;
  const showAdminSettings = tenantSettingsPath !== null;

  // User settings dialog state (only used when no full-page settings path)
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);

  // Document upload dialog state (accounting module)

  // AI Agents data
  const { data: agentsData, isLoading: agentsLoading } = useAgents(
    { is_active: true },
  );
  const { data: avatarsData } = useAvailableAvatars();

  // Tasks data — always fetch for module picker search
  const { data: workspacesData, isLoading: workspacesLoading } = useWorkspaces();
  const { data: boardsData, isLoading: boardsLoading } = useBoards();
  const { data: sharedBoardsData, isLoading: sharedBoardsLoading } = useSharedBoards();
  const deleteWorkspace = useDeleteWorkspace();
  const [workspaceToDelete, setWorkspaceToDelete] = useState<{ id: string; name: string; boardCount: number } | null>(null);
  const [boardToDelete, setBoardToDelete] = useState<{ id: string; name: string } | null>(null);

  // Tasks onboarding checklist state
  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    localStorage.getItem(ONBOARDING_DISMISSED_KEY) === 'true',
  );
  const { data: dashboardData } = useWorkManagementDashboard();
  const totalTasks = dashboardData?.total_tasks ?? 0;
  const handleDismissOnboarding = () => {
    setOnboardingDismissed(true);
    localStorage.setItem(ONBOARDING_DISMISSED_KEY, 'true');
  };

  // Sales onboarding status (lightweight EXISTS queries, 5min cache)
  const { data: setupStatus } = useSalesSetupStatus({ enabled: isSalesModule });
  const salesSetupComplete = !setupStatus ||
    (setupStatus.has_companies && setupStatus.has_contacts && setupStatus.has_deals);

  // Inject Get Started nav item for Sales module when setup is not yet complete
  const salesGetStartedItem: NavItem = {
    labelKey: 'nav.pipeline.getStarted',
    descriptionKey: 'nav.pipeline.getStartedDescription',
    path: '/pipeline/get-started',
    icon: <Rocket className="h-4 w-4" />,
    alwaysShowInNav: true,
    // Injected into the current module's nav list, so it is gated as one of its
    // items (isNavItemVisible reads `permissionModules` for requiresModuleAdmin).
    permissionModules: currentModule.permissionModules,
  };
  // Search-index entries are dropped ONCE, here, so every render site below
  // inherits it — a `searchOnly` item answers a query and is never a sidebar
  // row (see NavItem.searchOnly). Filtered AFTER the Sales injection so
  // `navItems[0]` still names the row the Get Started item is inserted beneath,
  // and `isSelected`'s longest-match walk deliberately keeps reading
  // `currentModule.navItems` unfiltered — a searchOnly page is still a real
  // destination, so it must still be able to out-specify a shorter sibling path
  // and stop that sibling's row from lighting up while the user is on it.
  const effectiveNavItems: NavItem[] = (
    isSalesModule && !salesSetupComplete
      ? [currentModule.navItems[0], salesGetStartedItem, ...currentModule.navItems.slice(1)]
      : currentModule.navItems
  ).filter(isListedNavItem);

  const agents = agentsData?.data || [];
  const avatars = avatarsData?.data || [];
  const workspaces = workspacesData?.data || [];
  const boards = boardsData?.data || [];
  const sharedBoards = sharedBoardsData?.data || [];

  // Build dynamic items for module picker search
  const dynamicItems = useMemo<DynamicPickerItem[]>(() => {
    const agentsList = agentsData?.data || [];
    const workspacesList = workspacesData?.data || [];
    const boardsList = boardsData?.data || [];
    const sharedBoardsList = sharedBoardsData?.data || [];
    const items: DynamicPickerItem[] = [];
    const agentsCategory = t('entities.agents');
    for (const agent of agentsList) {
      items.push({
        label: agent.name,
        path: `/ai-agents/${agent.id}`,
        moduleId: 'ai',
        category: agentsCategory,
      });
    }
    const workspacesCategory = t('entities.workspaces');
    for (const ws of workspacesList) {
      items.push({
        label: ws.name,
        path: `/tasks/workspaces/${ws.id}`,
        moduleId: 'tasks',
        category: workspacesCategory,
      });
    }
    const boardsCategory = t('entities.boards');
    const seenBoardIds = new Set<string>();
    for (const board of [...boardsList, ...sharedBoardsList]) {
      if (seenBoardIds.has(board.id)) continue;
      seenBoardIds.add(board.id);
      items.push({
        label: board.name,
        path: `/tasks/boards/${board.id}`,
        moduleId: 'tasks',
        category: boardsCategory,
      });
    }
    return items;
  }, [agentsData, workspacesData, boardsData, sharedBoardsData, t]);

  // Expanded state for workspaces and shared boards section — default all expanded.
  //
  // Synced DURING RENDER rather than in an effect. The sidebar is on every page, so
  // an effect — which runs AFTER the commit — meant every load COMMITTED one painted
  // frame with the workspaces fetched and every one of them drawn COLLAPSED, the
  // exact opposite of the intended default, before the second commit expanded them.
  // A `setState` during render makes React re-render before committing, so that
  // frame is never painted (`shared/hooks/useSyncedState.ts` has the long form).
  //
  // The marker is STATE, not the ref this used to keep: a ref written during render
  // survives a render React THROWS AWAY (navigation here goes through
  // `startTransition`) while the `setExpandedWorkspaces` it guarded does not, and
  // the sidebar would then stay collapsed for good. It compares CONTENT — the sorted
  // id list — so a refetch returning the same workspaces does not re-expand the ones
  // the user just collapsed.
  const workspaceIds = workspaces.map(ws => ws.id).sort().join(',');
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set());
  const [expandedForWorkspaceIds, setExpandedForWorkspaceIds] = useState('');
  if (workspaceIds && workspaceIds !== expandedForWorkspaceIds) {
    setExpandedForWorkspaceIds(workspaceIds);
    setExpandedWorkspaces(new Set(workspaces.map(ws => ws.id)));
  }


  const { resolvedTheme } = useThemeMode();
  const isDarkMode = resolvedTheme === 'dark';

  // Get panel background color
  const panelBg = getSecondaryPanelBg(currentModule.color, isDarkMode);

  // Whether to show text labels (expanded state)
  const showLabels = !isCollapsed;

  const getAvatarUrl = (avatarId: string | null): string | undefined => {
    if (!avatarId) return undefined;
    return avatars.find((a) => a.id === avatarId)?.preview_url;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  // Every nav ENTRY is an `<AppNavLink>` (a real `<a href>`, so the browser's own
  // middle-click / Cmd-click / right-click "Open in new tab" reach it); this is the
  // side effect that used to live in `handleNavigation`'s tail. It is passed as
  // `onNavigate`, which the link calls only when it navigated in THIS window — a
  // new-tab click leaves the drawer alone, which is what the user asked for.
  // `handleNavigation` stays for the PROGRAMMATIC navigations (post-delete redirects,
  // dropdown-menu entries) that are not links.
  const closeTemporaryDrawer =
    variant === 'temporary' && onClose ? () => onClose() : undefined;

  // ...and this covers the navigation the entry's own handler never sees. A dirty
  // form's prompt is raised by `useUnsavedChangesWarning`'s capture-phase anchor
  // interceptor, which stops the event before React's listener runs, and the
  // eventual navigate() comes from that form's dialog — so `onNavigate` above is
  // not called and the drawer would sit over the page just navigated to. Gated on
  // `open`, because `onClose` is a TOGGLE at the call site: firing it while the
  // drawer is already shut would open it.
  const lastPathnameRef = useRef(location.pathname);
  useEffect(() => {
    if (lastPathnameRef.current === location.pathname) return;
    lastPathnameRef.current = location.pathname;
    if (variant === 'temporary' && open) onClose?.();
  }, [location.pathname, variant, open, onClose]);

  const handleModuleSelect = (module: ModuleConfig) => {
    if (isModuleEnabledForUser(module.id)) {
      recordModuleVisit(module.id);
      switchModule(module.id);
      setDropdownOpen(false);
      setModuleSearchQuery('');
    }
  };

  const handlePageSelect = (path: string, module: ModuleConfig) => {
    if (isModuleEnabledForUser(module.id)) {
      recordModuleVisit(module.id);
      navigate(path);
      setDropdownOpen(false);
      setModuleSearchQuery('');
      if (variant === 'temporary' && onClose) {
        onClose();
      }
    }
  };

  const handleHomeSelect = () => {
    navigate('/');
    setDropdownOpen(false);
    setModuleSearchQuery('');
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const handleToggleFavorite = (moduleId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(moduleId);
  };

  // The Jobs Portal URL for the current tenant (null until the tenant is known).
  const jobsPortalUrl = user?.tenant_slug
    ? `${getWorkspaceOrigin()}/careers/${user.tenant_slug}`
    : null;

  const isSelected = (path: string) => {
    const pathname = location.pathname;

    // Exact match
    if (pathname === path) {
      return true;
    }

    // Prefix match: path must be followed by '/' to avoid /products matching /products/price-lists
    if (pathname.startsWith(path + '/')) {
      const allNavPaths = currentModule.navItems.map(item => item.path);
      const longerMatch = allNavPaths.find(
        navPath => navPath !== path &&
                   navPath.startsWith(path) &&
                   (pathname === navPath || pathname.startsWith(navPath + '/'))
      );
      return !longerMatch;
    }

    return false;
  };

  // Toggle workspace expansion
  const toggleWorkspace = (workspaceId: string) => {
    setExpandedWorkspaces((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(workspaceId)) {
        newSet.delete(workspaceId);
      } else {
        newSet.add(workspaceId);
      }
      return newSet;
    });
  };

  // Get board icon based on board type
  const getBoardIcon = (boardType?: string) => {
    if (boardType === 'okr') return Target;
    if (boardType === 'sprint') return Timer;
    return ClipboardList;
  };

  // Group boards by workspace
  const getBoardsForWorkspace = (workspaceId: string | null) => {
    return boards.filter((board) => board.workspace_id === workspaceId);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleGoForward = () => {
    window.history.forward();
  };

  // =============================================================================
  // NAV ITEM RENDERING
  // =============================================================================

  // Render nav item with module-colored selected state.
  // Purely presentational — it applies no access gate, so the hand-built items
  // below need not carry the module identity `requiresModuleAdmin` resolves
  // with. Items that ARE gated pass through `isNavItemVisible` before reaching
  // here (see renderDefaultNav).
  const renderSecondaryNavItem = (item: Omit<NavItem, 'permissionModules'>) => {
    const selected = isSelected(item.path);
    const isCrossModule = Boolean(item.crossModule);
    const sourceModule = item.crossModule ? getModuleById(item.crossModule) : undefined;
    const sourceModuleName = sourceModule ? getModuleName(sourceModule) : undefined;
    return (
      <Tooltip key={item.path}>
        <TooltipTrigger asChild>
          <AppNavLink
            to={item.path}
            onNavigate={closeTemporaryDrawer}
            data-testid={isCrossModule ? `nav-cross-module-${item.crossModule}` : undefined}
            className={cn(
              'flex items-center w-full rounded-md transition-all duration-200',
              showLabels ? 'gap-3 px-4 py-2' : 'justify-center px-2 py-2.5',
              selected
                ? NAV_ITEM_SELECTED
                : 'hover:bg-black/5 dark:hover:bg-white/5'
            )}
          >
            <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{item.icon}</span>
            {showLabels && (
              <>
                <BidiText className="text-sm truncate flex-1 text-start">{t(item.labelKey)}</BidiText>
                {isCrossModule && sourceModule && (
                  <span
                    className="shrink-0 opacity-60 [&>svg]:h-3.5 [&>svg]:w-3.5"
                    style={{ color: sourceModule.color }}
                    aria-hidden="true"
                  >
                    {sourceModule.icon}
                  </span>
                )}
              </>
            )}
          </AppNavLink>
        </TooltipTrigger>
        {(!showLabels || item.descriptionKey || isCrossModule) && (
          <TooltipContent side="right" className="max-w-xs">
            {t(item.labelKey)}
            {item.descriptionKey && showLabels && (
              <> — {t(item.descriptionKey)}</>
            )}
            {isCrossModule && sourceModuleName && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {t('nav.crossModuleEmbedded', { module: sourceModuleName })}
              </div>
            )}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  // Render agent item
  const renderAgentItem = (agent: { id: string; name: string; title: string | null; avatar_id: string | null }) => {
    const path = `/ai-agents/${agent.id}`;
    const selected = isSelected(path);
    return (
      <Tooltip key={agent.id}>
        <TooltipTrigger asChild>
          <AppNavLink
            to={path}
            onNavigate={closeTemporaryDrawer}
            className={cn(
              'flex items-center w-full rounded-md transition-all duration-200',
              showLabels ? 'gap-3 px-4 py-2' : 'justify-center px-2 py-2.5',
              selected
                ? NAV_ITEM_SELECTED
                : 'hover:bg-black/5 dark:hover:bg-white/5'
            )}
          >
            <Avatar className={showLabels ? 'h-7 w-7' : 'h-6 w-6'}>
              <AvatarImage src={getAvatarUrl(agent.avatar_id)} alt={agent.name} />
              <AvatarFallback className="text-xs">{agent.name.charAt(0)}</AvatarFallback>
            </Avatar>
            {showLabels && (
              <div className="flex-1 min-w-0 text-start">
                <p dir={autoDirection(agent.name, direction)} className="truncate font-medium text-sm">{agent.name}</p>
                {agent.title && (
                  <p dir={autoDirection(agent.title, direction)} className="truncate text-xs text-muted-foreground">
                    {agent.title}
                  </p>
                )}
              </div>
            )}
          </AppNavLink>
        </TooltipTrigger>
        {!showLabels && (
          <TooltipContent side="right">
            {agent.name}{agent.title && ` — ${agent.title}`}
          </TooltipContent>
        )}
      </Tooltip>
    );
  };

  // Render AI Agents module navigation
  const renderAIAgentsNav = () => {
    return (
    <div className="space-y-1 px-2">
      {/* Crew dashboard (module landing page) */}
      {renderSecondaryNavItem({
        labelKey: 'nav.aiAgents.dashboard',
        path: '/ai-agents/dashboard',
        icon: <LayoutDashboard className="h-4 w-4" />,
      })}
      {/* Agents list link */}
      {renderSecondaryNavItem({
        labelKey: 'nav.aiAgents.agents',
        path: '/ai-agents',
        icon: <Bot className="h-4 w-4" />,
      })}
      {/* Tenant-wide approval inbox with live open-count badge */}
      <ApprovalsNavItem
        showLabels={showLabels}
        selected={isSelected('/ai-agents/approvals')}
        path="/ai-agents/approvals"
        onNavigate={closeTemporaryDrawer}
      />
      <Separator className="my-2" />
      {/* Agents label */}
      {showLabels && (
        <p className={cn(NAV_SECTION_LABEL, 'px-2')}>
          {t('nav.aiAgents.agents')}
        </p>
      )}
      {/* Loading state */}
      {agentsLoading && (
        <div className={cn('space-y-2', showLabels ? 'px-4' : 'flex flex-col items-center')}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className={showLabels ? 'h-10 w-full' : 'h-6 w-6 rounded-full'} />
          ))}
        </div>
      )}
      {/* Agent items */}
      {!agentsLoading && agents.map((agent) => renderAgentItem(agent))}
      {/* Empty state */}
      {!agentsLoading && agents.length === 0 && showLabels && (
        <p className="px-4 py-2 text-sm text-muted-foreground">
          {t('nav.aiAgents.noAgents')}
        </p>
      )}
    </div>
  );
  };

  // Render Tasks module navigation with collapsible workspaces
  const renderWorkManagementNav = () => {
    // Boards with no workspace + boards whose workspace isn't in the user's list
    // Exclude shared boards (they're added separately to avoid duplicates)
    const workspaceIds = new Set(workspaces.map((w) => w.id));
    const sharedBoardIds = new Set(sharedBoards.map((b) => b.id));
    const unassignedBoards = boards.filter(
      (b) => (!b.workspace_id || !workspaceIds.has(b.workspace_id)) && !sharedBoardIds.has(b.id)
    );
    const isLoading = workspacesLoading || boardsLoading;

    return (
      <div className="space-y-1 px-2">
        {/* Dashboard link */}
        {renderSecondaryNavItem({
          labelKey: 'nav.dashboard',
          path: '/tasks/dashboard',
          icon: <LayoutDashboard className="h-4 w-4" />,
        })}
        {/* My Items link */}
        {renderSecondaryNavItem({
          labelKey: 'nav.tasks',
          path: '/tasks/tasks',
          icon: <ClipboardList className="h-4 w-4" />,
        })}
        <Separator className="my-2" />
        {/* Workspaces label */}
        {showLabels && (
          <p className={cn(NAV_SECTION_LABEL, 'px-2')}>
            {t('nav.workspaces')}
          </p>
        )}
        {/* All Workspaces link removed — users navigate directly to workspaces */}
        {/* Loading state */}
        {isLoading && (
          <div className={cn('space-y-2', showLabels ? 'px-4' : 'flex flex-col items-center')}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className={showLabels ? 'h-8 w-full' : 'h-5 w-5 rounded'} />
            ))}
          </div>
        )}
        {/* Workspaces with nested boards */}
        {!isLoading && workspaces.map((workspace) => {
          const workspaceBoards = getBoardsForWorkspace(workspace.id);
          const isExpanded = expandedWorkspaces.has(workspace.id);
          const workspacePath = `/tasks/workspaces/${workspace.id}`;
          const workspaceSelected = location.pathname === workspacePath;

          if (!showLabels) {
            // Collapsed: just show icon
            return (
              <Tooltip key={workspace.id}>
                <TooltipTrigger asChild>
                  <AppNavLink
                    to={workspacePath}
                    onNavigate={closeTemporaryDrawer}
                    className={cn(
                      'w-full flex items-center justify-center py-2.5 transition-colors',
                      workspaceSelected
                        ? NAV_ITEM_SELECTED
                        : 'hover:bg-black/5 dark:hover:bg-white/5'
                    )}
                    style={{
                      borderRadius: workspaceSelected ? '6px' : undefined,
                    }}
                  >
                    <Folder className="h-4 w-4" />
                  </AppNavLink>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {workspace.name}
                </TooltipContent>
              </Tooltip>
            );
          }

          // Expanded: show full workspace with collapsible boards
          return (
            <Collapsible
              key={workspace.id}
              open={isExpanded}
              onOpenChange={() => toggleWorkspace(workspace.id)}
            >
              <div
                className={cn(
                  'group/workspace flex items-center min-w-0 w-full rounded-md transition-all duration-200',
                  workspaceSelected
                    ? NAV_ITEM_SELECTED
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                )}
              >
                <AppNavLink
                  to={workspacePath}
                  onNavigate={closeTemporaryDrawer}
                  className="flex-1 min-w-0 flex items-center gap-3 px-4 py-2 text-sm"
                >
                  {isExpanded ? (
                    <FolderOpen className="h-4 w-4 shrink-0" />
                  ) : (
                    <Folder className="h-4 w-4 shrink-0" />
                  )}
                  <BidiText className="truncate">{workspace.name}</BidiText>
                </AppNavLink>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        'h-7 w-7 p-0 shrink-0 rounded-md opacity-0 group-hover/workspace:opacity-100 data-[state=open]:opacity-100 transition-opacity',
                        'hover:bg-black/10 dark:hover:bg-white/10'
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    onClick={(e) => e.stopPropagation()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                  >
                    <DropdownMenuItem
                      onClick={() => handleNavigation(`/tasks/boards/new?workspace=${workspace.id}`)}
                    >
                      <Plus className="h-4 w-4 me-2" />
                      {t('tasks:workspace.addBoard')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleNavigation(`/tasks/workspaces/${workspace.id}/edit`)}
                    >
                      <Pencil className="h-4 w-4 me-2" />
                      {t('tasks:workspace.editSettings')}
                    </DropdownMenuItem>
                    <ShareMenuItem
                      objectType="tasks.workspace"
                      objectId={workspace.id}
                      entityName={workspace.name}
                    />
                    <DropdownMenuItem
                      onClick={() => setWorkspaceToDelete({ id: workspace.id, name: workspace.name, boardCount: getBoardsForWorkspace(workspace.id).length })}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 me-2" />
                      {t('common:app.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'h-7 w-7 p-0 me-1 shrink-0 rounded-md',
                      'hover:bg-black/10 dark:hover:bg-white/10'
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent className="space-y-1">
                {workspaceBoards.map((board) => {
                  const boardPath = `/tasks/boards/${board.id}`;
                  const boardSelected = isSelected(boardPath);
                  const BoardIcon = getBoardIcon(board.board_type);
                  return (
                    <div
                      key={board.id}
                      className={cn(
                        'group/board flex items-center min-w-0 w-full rounded-md transition-all duration-200',
                        boardSelected
                          ? NAV_ITEM_SELECTED
                          : 'hover:bg-black/5 dark:hover:bg-white/5'
                      )}
                    >
                      <AppNavLink
                        to={boardPath}
                        onNavigate={closeTemporaryDrawer}
                        className="flex-1 min-w-0 flex items-center gap-3 pl-8 pr-1 py-1.5 text-sm"
                      >
                        <BoardIcon
                          className="h-4 w-4 shrink-0"
                          style={{ color: board.color || undefined }}
                        />
                        <BidiText className="truncate">{board.name}</BidiText>
                      </AppNavLink>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn(
                              'h-6 w-6 p-0 me-1 shrink-0 rounded-md opacity-0 group-hover/board:opacity-100 data-[state=open]:opacity-100 transition-opacity',
                              'hover:bg-black/10 dark:hover:bg-white/10'
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                          onCloseAutoFocus={(e) => e.preventDefault()}
                        >
                          <DropdownMenuItem
                            onClick={() => handleNavigation(`/tasks/boards/${board.id}/settings`)}
                          >
                            <Pencil className="h-4 w-4 me-2" />
                            {t('tasks:boards.boardSettings')}
                          </DropdownMenuItem>
                          <ShareMenuItem
                            objectType="tasks.board"
                            objectId={board.id}
                            entityName={board.name}
                          />
                          <DropdownMenuItem
                            onClick={() => setBoardToDelete({ id: board.id, name: board.name })}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 me-2" />
                            {t('common:app.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
                {workspace.sprint_settings?.enabled && (() => {
                  const sprintsPath = `/tasks/workspaces/${workspace.id}/sprints`;
                  const sprintsSelected = isSelected(sprintsPath);
                  return (
                    <AppNavLink
                      to={sprintsPath}
                      onNavigate={closeTemporaryDrawer}
                      className={cn(
                        'flex items-center gap-3 pl-8 pr-4 py-1.5 text-sm w-full rounded-md transition-all duration-200',
                        sprintsSelected
                          ? NAV_ITEM_SELECTED
                          : 'hover:bg-black/5 dark:hover:bg-white/5'
                      )}
                    >
                      <Timer className="h-4 w-4" />
                      <span className="truncate">{t('tasks:sprints.title')}</span>
                    </AppNavLink>
                  );
                })()}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        {/* Other boards (unassigned + shared) */}
        {!isLoading && !sharedBoardsLoading && (unassignedBoards.length > 0 || sharedBoards.length > 0) && showLabels && (
          <>
            <p className={cn(NAV_SECTION_LABEL, 'mt-2 px-2')}>
              {t('nav.work.unassignedBoards')}
            </p>
            {[...unassignedBoards, ...sharedBoards].map((board) => {
              const boardPath = `/tasks/boards/${board.id}`;
              const boardSelected = isSelected(boardPath);
              const BoardIcon = getBoardIcon(board.board_type);
              const isShared = sharedBoards.some((b) => b.id === board.id);
              return (
                <div
                  key={board.id}
                  className={cn(
                    'group/board flex items-center min-w-0 w-full rounded-md transition-all duration-200',
                    boardSelected
                      ? NAV_ITEM_SELECTED
                      : 'hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                >
                  <AppNavLink
                    to={boardPath}
                    onNavigate={closeTemporaryDrawer}
                    className="flex-1 min-w-0 flex items-center gap-3 px-4 py-1.5 text-sm"
                  >
                    <BoardIcon
                      className="h-4 w-4 shrink-0"
                      style={{ color: board.color || undefined }}
                    />
                    <BidiText className="truncate">{board.name}</BidiText>
                    {isShared && (
                      <Users className="h-3 w-3 shrink-0 opacity-50" />
                    )}
                  </AppNavLink>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          'h-6 w-6 p-0 me-1 shrink-0 rounded-md opacity-0 group-hover/board:opacity-100 data-[state=open]:opacity-100 transition-opacity',
                          'hover:bg-black/10 dark:hover:bg-white/10'
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      onClick={(e) => e.stopPropagation()}
                      onCloseAutoFocus={(e) => e.preventDefault()}
                    >
                      <DropdownMenuItem
                        onClick={() => handleNavigation(`/tasks/boards/${board.id}/settings`)}
                      >
                        <Pencil className="h-4 w-4 me-2" />
                        {t('tasks:boards.boardSettings')}
                      </DropdownMenuItem>
                      <ShareMenuItem
                        objectType="tasks.board"
                        objectId={board.id}
                        entityName={board.name}
                      />
                      <DropdownMenuItem
                        onClick={() => setBoardToDelete({ id: board.id, name: board.name })}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 me-2" />
                        {t('common:app.delete')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </>
        )}
        {/* Empty state */}
        {!isLoading && workspaces.length === 0 && unassignedBoards.length === 0 && sharedBoards.length === 0 && showLabels && (
          <p className="px-4 py-2 text-sm text-muted-foreground">
            {t('nav.work.noWorkspaces')}
          </p>
        )}
      </div>
    );
  };

  // Render default module navigation
  const renderDefaultNav = () => (
    <div className="space-y-1 px-2">
      {effectiveNavItems
        .filter(item => currentModule.pinned || item.alwaysShowInNav || item.path !== currentModule.defaultPath)
        .filter(item => isNavItemVisible(item, navItemAccess))
        .map((item, index, filteredItems) => {
        const prevSection = index > 0 ? filteredItems[index - 1].section : undefined;
        const isNewSection = item.section && item.section !== prevSection;
        return (
          <Fragment key={item.path}>
            {isNewSection && showLabels && (
              <>
                <Separator className="my-2" />
                <p className={cn(NAV_SECTION_LABEL, 'px-2')}>
                  {t(item.section!)}
                </p>
              </>
            )}
            {renderSecondaryNavItem(item)}
          </Fragment>
        );
      })}
      {/* Jobs Portal link for Recruiting module */}
      {isRecruitingModule && user?.tenant_slug && jobsPortalUrl && (
        <>
          <Separator className="my-2" />
          {/* An external destination, so it is an anchor for the same reason every
              entry above is one — plus `target="_blank"`, which AppNavLink hands
              straight to the browser rather than routing in-app. */}
          <AppNavLink
            to={jobsPortalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'w-full flex items-center rounded-md transition-all duration-200',
              showLabels ? 'gap-3 px-4 py-2' : 'justify-center px-2 py-2.5',
              'hover:bg-black/5 dark:hover:bg-white/5'
            )}
          >
            <Globe className="h-4 w-4 shrink-0" />
            {showLabels && (
              <>
                <span className="flex-1 text-sm truncate text-start">{t('nav.recruiting.jobsPortal')}</span>
                <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
              </>
            )}
          </AppNavLink>
        </>
      )}
    </div>
  );

  // =============================================================================
  // SINGLE-PANEL SIDEBAR
  // =============================================================================

  const renderSidebarInner = (hasNavItems: boolean) => (
    <>
      {/* Module switcher at top — Linear style */}
      <div className={cn('shrink-0 pt-3 pb-2', isCollapsed ? 'px-1' : 'px-3')}>
        {isDesktopApp && !isCollapsed && (
          <div className="flex items-center gap-1 mb-2">
            <button onClick={handleGoBack} className="p-1 rounded hover:bg-accent/50">
              <ArrowLeft className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
            <button onClick={handleGoForward} className="p-1 rounded hover:bg-accent/50">
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
        )}
        {isCollapsed || !showLabels ? (
          <ModuleSwitcher
            trigger={
              <button
                className="flex items-center w-full justify-center p-2 min-h-11 rounded-md hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                aria-label={t('modules.switchModule')}
                data-testid="module-switcher"
              >
                <div className="w-6 h-6 rounded flex items-center justify-center shrink-0 bg-module-accent/15">
                  <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 text-module-accent">
                    {currentModule.icon}
                  </span>
                </div>
              </button>
            }
            align="start"
          />
        ) : (
          <div className="flex items-center w-full rounded-md hover:bg-black/5 dark:hover:bg-white/5 transition-colors pl-1">
            <Tooltip>
              <ModuleSwitcher
                trigger={
                  <TooltipTrigger asChild>
                    <button
                      className="p-1.5 min-h-11 min-w-11 flex items-center justify-center rounded hover:bg-black/10 dark:hover:bg-white/10 shrink-0"
                      aria-label={t('modules.switchModule')}
                      data-testid="module-switcher"
                    >
                      <Grip className="h-5 w-5 text-foreground" />
                    </button>
                  </TooltipTrigger>
                }
                align="start"
              />
              <TooltipContent side="bottom">{t('modules.switchModule')}</TooltipContent>
            </Tooltip>
            <AppNavLink
              to={currentModule.defaultPath}
              className="flex items-center gap-2.5 flex-1 min-w-0 min-h-11 px-1 py-1.5 rounded-md text-start"
              title={t('modules.goToModuleHome', { module: getModuleName(currentModule) })}
            >
              <div className="w-6 h-6 rounded flex items-center justify-center shrink-0 bg-module-accent/15">
                <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 text-module-accent">
                  {currentModule.icon}
                </span>
              </div>
              <span className="text-sm font-semibold truncate">{getModuleName(currentModule)}</span>
            </AppNavLink>
          </div>
        )}
      </div>

      {/* Module-specific navigation */}
      {hasNavItems && (
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-2">
          {isAIAgentsModule ? renderAIAgentsNav() : isWorkModule ? renderWorkManagementNav() : renderDefaultNav()}
        </div>
      )}

      {/* Tasks onboarding checklist */}
      {isWorkModule && !onboardingDismissed && showLabels && (
        <div className="shrink-0 px-3 pb-2" data-testid="tasks-onboarding-checklist">
          <div className="bg-card border border-border rounded-lg p-3 shadow-sm">
            <div className="flex flex-col gap-2.5">
              {[
                {
                  id: 'workspace' as const,
                  label: t('tasks:dashboard.onboarding.createWorkspace'),
                  done: workspaces.length > 0,
                  path: '/tasks/workspaces/new',
                },
                {
                  id: 'board' as const,
                  label: t('tasks:dashboard.onboarding.noBoardsTitle'),
                  done: boards.length > 0,
                  path: workspaces.length > 0 ? '/tasks/workspaces' : undefined,
                },
                {
                  id: 'task' as const,
                  label: t('tasks:dashboard.onboarding.createTask'),
                  done: totalTasks > 0,
                  path: boards.length > 0 ? '/tasks/tasks' : undefined,
                },
              ].map((step) => {
                const stepClassName = cn(
                  'flex items-center gap-2 text-sm text-start transition-colors',
                  step.done
                    ? 'text-muted-foreground line-through'
                    : step.path
                      ? 'text-foreground cursor-pointer hover:text-primary'
                      : 'text-muted-foreground/60 cursor-default',
                );
                const stepBody = (
                  <>
                    <div
                      className={cn(
                        'h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors',
                        step.done
                          ? 'bg-foreground border-foreground'
                          : 'border-border',
                      )}
                    >
                      {step.done && <Check className="h-3 w-3 text-background" />}
                    </div>
                    {step.label}
                  </>
                );
                // A live step goes somewhere, so it is a link like every other entry.
                // A done or not-yet-reachable one is inert — and an anchor has no
                // `disabled`, so it renders as plain text rather than as a link the
                // browser would happily open in a new tab.
                return step.path && !step.done ? (
                  <AppNavLink
                    key={step.id}
                    to={step.path}
                    onNavigate={closeTemporaryDrawer}
                    className={stepClassName}
                  >
                    {stepBody}
                  </AppNavLink>
                ) : (
                  <div key={step.id} className={stepClassName}>
                    {stepBody}
                  </div>
                );
              })}
            </div>
            <button
              className="mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={handleDismissOnboarding}
            >
              {t('tasks:dashboard.onboarding.hide')}
            </button>
          </div>
        </div>
      )}

      {/* Module Settings */}
      <div className="mt-auto shrink-0">
        {(hasUserSettings || showAdminSettings) && (
          <div className="border-t py-2">
            <div className={cn(
              'w-full flex items-center transition-all duration-200',
              showLabels ? 'gap-2 px-4 py-2' : 'justify-center gap-1 px-2 py-2',
            )}>
              {hasUserSettings && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A Tooltip is NOT an accessible name: Radix wires it as a
                        description and only while it is open, so this icon-only
                        control announced as just "button" (axe button-name,
                        critical). Same key its mobile sibling below already uses.
                        A path-backed gear is a LINK (new-tab-able like every other
                        entry); the pathless one opens a dialog and stays a button. */}
                    {userSettingsPath ? (
                      <AppNavLink
                        to={userSettingsPath}
                        onNavigate={closeTemporaryDrawer}
                        aria-label={t('nav.moduleUserSettings', { module: getModuleName(currentModule) })}
                        className={cn(
                          'p-1 rounded transition-colors shrink-0',
                          userSettingsPathname && location.pathname === userSettingsPathname
                            ? NAV_ITEM_SELECTED_BG
                            : 'hover:bg-black/5 dark:hover:bg-white/5'
                        )}
                      >
                        <Settings className="h-4 w-4 text-module-accent" />
                      </AppNavLink>
                    ) : (
                      <button
                        onClick={() => setUserSettingsOpen(true)}
                        aria-label={t('nav.moduleUserSettings', { module: getModuleName(currentModule) })}
                        className="p-1 rounded transition-colors shrink-0 hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <Settings className="h-4 w-4 text-module-accent" />
                      </button>
                    )}
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {t('moduleSettings.title', { module: getModuleName(currentModule) })}
                  </TooltipContent>
                </Tooltip>
              )}
              {showAdminSettings && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AppNavLink
                      data-testid="module-admin-settings"
                      to={tenantSettingsPath}
                      onNavigate={closeTemporaryDrawer}
                      aria-label={t('nav.moduleAdminSettings', { module: getModuleName(currentModule) })}
                      className={cn(
                        'p-1 rounded transition-colors shrink-0',
                        location.pathname === tenantSettingsPath
                          ? NAV_ITEM_SELECTED_BG
                          : 'hover:bg-black/5 dark:hover:bg-white/5'
                      )}
                    >
                      <ShieldCheck className="h-4 w-4 text-module-accent" />
                    </AppNavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {t('moduleSettings.adminTitle', { module: getModuleName(currentModule) })}
                  </TooltipContent>
                </Tooltip>
              )}
              {showLabels && <span className="text-sm truncate">{t('nav.settings')}</span>}
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderSidebarPanel = () => {
    const filteredNavItems = effectiveNavItems
      .filter(item => currentModule.pinned || item.alwaysShowInNav || item.path !== currentModule.defaultPath)
      .filter(item => isNavItemVisible(item, navItemAccess));
    const hasNavItems = isAIAgentsModule || isWorkModule || filteredNavItems.length > 0;

    if (collapsedProp) {
      return (
        <div
          className="h-full shrink-0 w-14 relative"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            className={cn(
              'h-full flex flex-col transition-all duration-200 overflow-hidden',
              hovered
                ? cn(
                    'w-60 absolute z-50 shadow-xl top-0',
                    anchor === 'right' ? 'right-0 rounded-l-xl' : 'left-0 rounded-r-xl',
                  )
                : 'w-14',
            )}
            style={{ backgroundColor: panelBg }}
          >
            {renderSidebarInner(hasNavItems)}
          </div>
        </div>
      );
    }

    return (
      <div
        className="h-full flex flex-col shrink-0 w-60"
        style={{ backgroundColor: panelBg }}
      >
        {renderSidebarInner(hasNavItems)}
      </div>
    );
  };

  // =============================================================================
  // MOBILE DRAWER CONTENT (temporary variant)
  // =============================================================================

  // State for mobile module picker
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');

  const handleMobileModuleSelect = (module: ModuleConfig) => {
    if (isModuleEnabledForUser(module.id)) {
      recordModuleVisit(module.id);
      switchModule(module.id);
      setMobileDropdownOpen(false);
      setMobileSearchQuery('');
    }
  };

  const handleMobilePageSelect = (path: string, module: ModuleConfig) => {
    if (isModuleEnabledForUser(module.id)) {
      recordModuleVisit(module.id);
      navigate(path);
      setMobileDropdownOpen(false);
      setMobileSearchQuery('');
      if (onClose) {
        onClose();
      }
    }
  };

  const handleMobileHomeSelect = () => {
    navigate('/');
    setMobileDropdownOpen(false);
    setMobileSearchQuery('');
    if (onClose) {
      onClose();
    }
  };

  // Re-read localStorage when mobile dropdown opens
  useEffect(() => {
    if (mobileDropdownOpen) {
      refresh();
    } else {
      setMobileSearchQuery('');
    }
  }, [mobileDropdownOpen, refresh]);

  const renderMobileDrawerContent = () => {
    return (
      <div className="flex flex-col h-full">
        {/* App logo - match header height (h-12 = 48px) */}
        <div className="flex items-center gap-2 px-4 border-b h-12 shrink-0">
          <AppNavLink
            to="/"
            onNavigate={() => onClose?.()}
            className="flex items-center gap-2 rounded hover:bg-accent transition-colors px-1 h-10 touch:min-h-[44px]"
            aria-label={appConfig.app.name}
          >
            <img src="/logo-64.png" alt={appConfig.app.name} className="h-8 w-8 shrink-0" />
            {/* eslint-disable-next-line i18next/no-literal-string -- brand name */}
            <span className="font-semibold text-base text-foreground">PanLuma.ai</span>
          </AppNavLink>
        </div>

        {/* Module dropdown trigger */}
        <div className="relative">
          <div
            className="w-full flex items-center border-b-2 bg-module-accent/[0.08] border-b-module-accent"
          >
            {/* Module name + icon — opens picker (same as desktop) */}
            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="flex-1 min-w-0 px-3 py-3 flex items-center gap-2.5 hover:bg-accent/50 transition-colors"
              aria-label={t('modules.switchModule')}
              data-testid="module-switcher"
            >
              <Grip className="h-5 w-5 text-foreground shrink-0" />
              <div className="flex items-center justify-center w-8 h-8 rounded shrink-0 bg-module-accent/15">
                <span className="[&>svg]:h-4 [&>svg]:w-4 text-module-accent">
                  {currentModule.icon}
                </span>
              </div>
              <span className="font-semibold flex-1 text-start truncate text-module-accent">
                {getModuleName(currentModule)}
              </span>
            </button>
            {showAdminSettings && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* The button this replaced called `stopPropagation()`; it is not
                      carried over because nothing above these gears listens for a
                      click — they are SIBLINGS of the module-switcher trigger in
                      this row, and the Sheet closes on `onOpenChange`, not on a
                      bubbled click. */}
                  <Button
                    asChild
                    data-testid="module-admin-settings"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-module-accent"
                    aria-label={t('nav.moduleAdminSettings', { module: getModuleName(currentModule) })}
                  >
                    <AppNavLink
                      to={tenantSettingsPath}
                      onNavigate={() => onClose?.()}
                    >
                      <ShieldCheck className="h-4 w-4" />
                    </AppNavLink>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('moduleSettings.adminTitle', { module: getModuleName(currentModule) })}
                </TooltipContent>
              </Tooltip>
            )}
            {hasUserSettings && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Path-backed gear is a link (new-tab-able); the pathless one
                      opens a dialog and stays a button — same split as desktop. */}
                  <Button
                    asChild={Boolean(userSettingsPath)}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 mr-2 text-module-accent"
                    onClick={userSettingsPath ? undefined : (e) => {
                      e.stopPropagation();
                      setUserSettingsOpen(true);
                    }}
                    aria-label={t('nav.moduleUserSettings', { module: getModuleName(currentModule) })}
                  >
                    {userSettingsPath ? (
                      <AppNavLink
                        to={userSettingsPath}
                        onNavigate={() => onClose?.()}
                      >
                        <Settings className="h-4 w-4" />
                      </AppNavLink>
                    ) : (
                      <Settings className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('moduleSettings.title', { module: getModuleName(currentModule) })}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Mobile module picker dropdown */}
          {mobileDropdownOpen && (
            <div className="border-b bg-popover">
              <ModulePickerContent
                pickerModules={pickerModules}
                disabledPickerModules={disabledPickerModules}
                searchQuery={mobileSearchQuery}
                onSearchChange={setMobileSearchQuery}
                isCurrentModule={isCurrentModule}
                isModuleEnabledForUser={isModuleEnabledForUser}
                onModuleSelect={handleMobileModuleSelect}
                onPageSelect={handleMobilePageSelect}
                onHomeSelect={handleMobileHomeSelect}
                isFavorite={isFavorite}
                onToggleFavorite={handleToggleFavorite}
                sortModules={sortModules}
                isOpen={mobileDropdownOpen}
                maxHeight="300px"
                pinnedModules={pinnedModules}
                dynamicItems={dynamicItems}
              />
            </div>
          )}
        </div>

        {/* Module navigation - always expanded in mobile */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-2" style={{ backgroundColor: panelBg }}>
          {/* Force expanded labels for mobile */}
          <div className="space-y-1 px-2">
            {isAIAgentsModule ? (
              // Inline agent nav for mobile (always show labels)
              <>
                <AppNavLink
                  to="/ai-agents/dashboard"
                  onNavigate={closeTemporaryDrawer}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                    isSelected('/ai-agents/dashboard')
                      ? NAV_ITEM_SELECTED
                      : 'hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>{t('nav.aiAgents.dashboard')}</span>
                </AppNavLink>
                <AppNavLink
                  to="/ai-agents"
                  onNavigate={closeTemporaryDrawer}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                    isSelected('/ai-agents')
                      ? NAV_ITEM_SELECTED
                      : 'hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                >
                  <Bot className="h-4 w-4" />
                  <span>{t('nav.aiAgents.agents')}</span>
                </AppNavLink>
                <Separator className="my-2" />
                <p className={cn(NAV_SECTION_LABEL, 'px-2')}>
                  {t('nav.aiAgents.agents')}
                </p>
                {agents.map((agent) => {
                  const path = `/ai-agents/${agent.id}`;
                  const selected = isSelected(path);
                  return (
                    <AppNavLink
                      key={agent.id}
                      to={path}
                      onNavigate={closeTemporaryDrawer}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                        selected ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                      )}
                    >
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={getAvatarUrl(agent.avatar_id)} alt={agent.name} />
                        <AvatarFallback className="text-xs">{agent.name.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 text-start">
                        <p dir={autoDirection(agent.name, direction)} className="truncate font-medium">{agent.name}</p>
                        {agent.title && (
                          <p dir={autoDirection(agent.title, direction)} className="truncate text-xs text-muted-foreground">
                            {agent.title}
                          </p>
                        )}
                      </div>
                    </AppNavLink>
                  );
                })}
              </>
            ) : isWorkModule ? (
              // Inline Work nav for mobile
              <>
                <AppNavLink
                  to="/tasks/dashboard"
                  onNavigate={closeTemporaryDrawer}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                    isSelected('/tasks/dashboard') ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>{t('nav.dashboard')}</span>
                </AppNavLink>
                <AppNavLink
                  to="/tasks/tasks"
                  onNavigate={closeTemporaryDrawer}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                    isSelected('/tasks/tasks') ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                >
                  <ClipboardList className="h-4 w-4" />
                  <span>{t('nav.tasks')}</span>
                </AppNavLink>
                <Separator className="my-2" />
                <p className={cn(NAV_SECTION_LABEL, 'px-2')}>
                  {t('nav.workspaces')}
                </p>
                {/* All Workspaces link removed — users navigate directly to workspaces */}
                {workspaces.map((workspace) => {
                  const path = `/tasks/workspaces/${workspace.id}`;
                  const selected = location.pathname === path;
                  const workspaceBoards = getBoardsForWorkspace(workspace.id);
                  const isExpanded = expandedWorkspaces.has(workspace.id);
                  return (
                    <Collapsible
                      key={workspace.id}
                      open={isExpanded}
                      onOpenChange={() => toggleWorkspace(workspace.id)}
                    >
                      <div
                        className={cn(
                          'group/workspace flex items-center min-w-0 w-full rounded-md transition-colors',
                          selected ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                        )}
                      >
                        <AppNavLink
                          to={path}
                          onNavigate={closeTemporaryDrawer}
                          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-2 text-sm min-h-11"
                        >
                          {isExpanded ? (
                            <FolderOpen className="h-4 w-4 shrink-0" />
                          ) : (
                            <Folder className="h-4 w-4 shrink-0" />
                          )}
                          <BidiText className="truncate">{workspace.name}</BidiText>
                        </AppNavLink>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                'h-7 w-7 p-0 shrink-0 rounded-md',
                                'hover:bg-black/10 dark:hover:bg-white/10'
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            onClick={(e) => e.stopPropagation()}
                            onCloseAutoFocus={(e) => e.preventDefault()}
                          >
                            <DropdownMenuItem
                              onClick={() => handleNavigation(`/tasks/boards/new?workspace=${workspace.id}`)}
                            >
                              <Plus className="h-4 w-4 me-2" />
                              {t('tasks:workspace.addBoard')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleNavigation(`/tasks/workspaces/${workspace.id}/edit`)}
                            >
                              <Pencil className="h-4 w-4 me-2" />
                              {t('tasks:workspace.editSettings')}
                            </DropdownMenuItem>
                            <ShareMenuItem
                              objectType="tasks.workspace"
                              objectId={workspace.id}
                              entityName={workspace.name}
                            />
                            <DropdownMenuItem
                              onClick={() => setWorkspaceToDelete({ id: workspace.id, name: workspace.name, boardCount: getBoardsForWorkspace(workspace.id).length })}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 me-2" />
                              {t('common:app.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        {workspaceBoards.length > 0 && (
                          <CollapsibleTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={cn(
                                'h-7 w-7 p-0 me-1 shrink-0 rounded-md',
                                'hover:bg-black/10 dark:hover:bg-white/10'
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                          </CollapsibleTrigger>
                        )}
                      </div>
                      <CollapsibleContent className="space-y-1">
                        {workspaceBoards.map((board) => {
                          const boardPath = `/tasks/boards/${board.id}`;
                          const boardSelected = isSelected(boardPath);
                          const BoardIcon = getBoardIcon(board.board_type);
                          return (
                            <div
                              key={board.id}
                              className={cn(
                                'flex items-center min-w-0 w-full rounded-md transition-colors min-h-11',
                                boardSelected ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                              )}
                            >
                              <AppNavLink
                                to={boardPath}
                                onNavigate={closeTemporaryDrawer}
                                className="flex-1 min-w-0 flex items-center gap-3 pl-8 pr-1 py-1.5 text-sm"
                              >
                                <BoardIcon
                                  className="h-4 w-4 shrink-0"
                                  style={{ color: board.color || undefined }}
                                />
                                <BidiText className="truncate">{board.name}</BidiText>
                              </AppNavLink>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={cn(
                                      'h-7 w-7 p-0 me-1 shrink-0 rounded-md',
                                      'hover:bg-black/10 dark:hover:bg-white/10'
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  onClick={(e) => e.stopPropagation()}
                                  onCloseAutoFocus={(e) => e.preventDefault()}
                                >
                                  <DropdownMenuItem
                                    onClick={() => handleNavigation(`/tasks/boards/${board.id}/settings`)}
                                  >
                                    <Pencil className="h-4 w-4 me-2" />
                                    {t('tasks:boards.boardSettings')}
                                  </DropdownMenuItem>
                                  <ShareMenuItem
                                    objectType="tasks.board"
                                    objectId={board.id}
                                    entityName={board.name}
                                  />
                                  <DropdownMenuItem
                                    onClick={() => setBoardToDelete({ id: board.id, name: board.name })}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 me-2" />
                                    {t('common:app.delete')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          );
                        })}
                        {workspace.sprint_settings?.enabled && (() => {
                          const sprintsPath = `/tasks/workspaces/${workspace.id}/sprints`;
                          const sprintsSelected = isSelected(sprintsPath);
                          return (
                            <AppNavLink
                              to={sprintsPath}
                              onNavigate={closeTemporaryDrawer}
                              className={cn(
                                'flex items-center gap-3 pl-8 pr-4 py-1.5 text-sm w-full rounded-md transition-colors min-h-11',
                                sprintsSelected ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                              )}
                            >
                              <Timer className="h-4 w-4" />
                              <span className="truncate">{t('tasks:sprints.title')}</span>
                            </AppNavLink>
                          );
                        })()}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
                {/* Shared boards appended after workspaces */}
                {sharedBoards.length > 0 && sharedBoards.map((board) => {
                  const path = `/tasks/boards/${board.id}`;
                  const selected = isSelected(path);
                  const BoardIcon = getBoardIcon(board.board_type);
                  return (
                    <AppNavLink
                      key={board.id}
                      to={path}
                      onNavigate={closeTemporaryDrawer}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                        selected ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                      )}
                    >
                      <BoardIcon
                        className="h-4 w-4"
                        style={{ color: selected ? 'white' : (board.color || undefined) }}
                      />
                      <BidiText className="truncate">{board.name}</BidiText>
                      <Users className="h-3 w-3 shrink-0 opacity-50" />
                    </AppNavLink>
                  );
                })}
              </>
            ) : (
              // Default nav items for mobile
              <>
                {(() => {
                  const mobileItems = effectiveNavItems
                    .filter(item => currentModule.pinned || item.alwaysShowInNav || item.path !== currentModule.defaultPath)
                    .filter(item => isNavItemVisible(item, navItemAccess));
                  return mobileItems.map((item, index) => {
                const selected = isSelected(item.path);
                const prevSection = index > 0 ? mobileItems[index - 1].section : undefined;
                const isNewSection = item.section && item.section !== prevSection;
                return (
                  <Fragment key={item.path}>
                    {isNewSection && (
                      <>
                        <Separator className="my-2" />
                        <p className={cn(NAV_SECTION_LABEL, 'px-2')}>
                          {t(item.section!)}
                        </p>
                      </>
                    )}
                    {(() => {
                      const sourceModule = item.crossModule ? getModuleById(item.crossModule) : undefined;
                      return (
                        <AppNavLink
                          to={item.path}
                          onNavigate={closeTemporaryDrawer}
                          data-testid={item.crossModule ? `nav-cross-module-${item.crossModule}` : undefined}
                          className={cn(
                            'flex items-center gap-3 px-4 py-2 text-sm w-full rounded-md transition-colors min-h-11',
                            selected ? NAV_ITEM_SELECTED : 'hover:bg-black/5 dark:hover:bg-white/5'
                          )}
                        >
                          <span className="[&>svg]:h-4 [&>svg]:w-4">{item.icon}</span>
                          <BidiText className="flex-1 text-start">{t(item.labelKey)}</BidiText>
                          {sourceModule && (
                            <span
                              className="shrink-0 opacity-60 [&>svg]:h-3.5 [&>svg]:w-3.5"
                              style={{ color: sourceModule.color }}
                              aria-hidden="true"
                            >
                              {sourceModule.icon}
                            </span>
                          )}
                        </AppNavLink>
                      );
                    })()}
                  </Fragment>
                );
              });
                })()}
              </>
            )}
          </div>
        </div>

      </div>
    );
  };

  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  // For temporary (mobile) drawer, use Sheet
  if (variant === 'temporary') {
    return (
      <>
        <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose?.()}>
          <SheetContent
            side={anchor}
            className="w-[280px] p-0"
            data-testid="mobile-sidebar-drawer"
          >
            <SheetTitle className="sr-only">{t('nav.navigation')}</SheetTitle>
            {renderMobileDrawerContent()}
          </SheetContent>
        </Sheet>

        {!userSettingsPath && (
          <ModuleUserSettingsDialog
            open={userSettingsOpen}
            onClose={() => setUserSettingsOpen(false)}
            moduleId={currentModule.id}
            moduleName={getModuleName(currentModule)}
            moduleColor={MODULE_ACCENT_COLOR}
          />
        )}
      </>
    );
  }

  // For permanent/persistent drawer, use single-panel sidebar (full height, in-flow)
  return (
    <>
      <TooltipProvider delayDuration={100} skipDelayDuration={300}>
        <aside
          className="h-full flex shrink-0"
          data-testid="main-navigation"
        >
          {renderSidebarPanel()}
        </aside>
      </TooltipProvider>

      <ModuleUserSettingsDialog
        open={userSettingsOpen}
        onClose={() => setUserSettingsOpen(false)}
        moduleId={currentModule.id}
        moduleName={getModuleName(currentModule)}
        moduleColor={MODULE_ACCENT_COLOR}
      />

      <ConfirmDialog
        open={!!workspaceToDelete}
        onClose={() => setWorkspaceToDelete(null)}
        title={t('tasks:workspace.deleteTitle')}
        message={t('tasks:workspace.deleteMessage', { name: workspaceToDelete?.name })}
        onConfirm={() => {
          if (workspaceToDelete) {
            deleteWorkspace.mutate(workspaceToDelete.id, {
              onSuccess: () => {
                toast.success(t('common:app.deleteSuccess'));
                setWorkspaceToDelete(null);
                handleNavigation('/tasks');
              },
              // The global mutation-error handler shows the specific translated toast.
            });
          }
        }}
        destructive
      >
        {workspaceToDelete && workspaceToDelete.boardCount > 0 && (
          <p className="text-sm text-muted-foreground mt-2">
            {t('tasks:workspace.deleteWarningBoards', {
              count: workspaceToDelete.boardCount,
              defaultWorkspace: workspaces.find(w => w.is_default)?.name ?? t('tasks:workspace.defaultWorkspace'),
            })}
          </p>
        )}
      </ConfirmDialog>

      {/* Board delete — shared deletion-impact dialog (keep vs delete tasks,
          default-board blocker), consistent with the other board-delete sites. */}
      <BoardDeleteDialog
        open={!!boardToDelete}
        onClose={() => setBoardToDelete(null)}
        board={boardToDelete}
        onDeleted={() => {
          toast.success(t('common:app.deleteSuccess'));
          handleNavigation('/tasks');
        }}
      />
    </>
  );
}

export { DRAWER_WIDTH };
